/**
 * Etajul desenat fara plan ("Draw without a plan", 30 septembrie 2026): o
 * foaie alba cu puncte, de marimea etajului, cu scara fixa. Functii pure -
 * harta (`FloorPlanMap.tsx`) le deseneaza, probele le verifica fara `ol`.
 *
 * Coordonatele sunt pixeli ai foii (ca ai unei imagini), de la coltul din
 * stanga-sus; `scale` = pixeli pe metru (la foaie: `CANVAS_SCALE`).
 */

/** Pixeli pe metru ai foii goale; tot aia o scrie si serverul (FloorLayout::CANVAS_SCALE). */
export const CANVAS_SCALE = 20
/** Marimea implicita a etajului desenat, in metri. */
export const CANVAS_DEFAULT_M = { width: 40, length: 25 }
/** Limitele laturii, in metri (FloorLayout::CANVAS_MIN_M / CANVAS_MAX_M). */
export const CANVAS_MIN_M = 5
export const CANVAS_MAX_M = 500

/** Un punct la fiecare metru, unul marcat la fiecare al cincilea. */
export const GRID_MAJOR_EVERY = 5
/** Sub atatia pixeli de ecran intre doua puncte, grila se rareste (x5). */
export const GRID_MIN_GAP = 6
/** Peretii se prind pe jumatate de metru. */
export const SNAP_M = 0.5

export type GridDot = { x: number; y: number; major: boolean }

/**
 * Pasul grilei, in metri, dupa cat de departe e harta: 1 m cat timp punctele
 * stau la cel putin `GRID_MIN_GAP` pixeli de ecran unul de altul, apoi 5 m,
 * 25 m... Punctul marcat e la fiecare al cincilea pas.
 *
 * @param zoom pixeli de ecran pe un pixel al foii
 */
export function gridStep(scale: number, zoom: number): { minorM: number; majorM: number } {
  let minor = 1
  if (scale > 0 && zoom > 0) {
    while (minor * scale * zoom < GRID_MIN_GAP && minor < 1e6) minor *= GRID_MAJOR_EVERY
  }
  return { minorM: minor, majorM: minor * GRID_MAJOR_EVERY }
}

/**
 * Punctele grilei care cad in `view` (pixeli ai foii; lipsa = toata foaia),
 * numai inauntrul etajului. Marginile foii (0 si latimea) primesc si ele
 * puncte cand cad pe pas.
 */
export function gridDots(o: { widthPx: number; heightPx: number; scale: number; zoom: number; view?: [number, number, number, number] }): GridDot[] {
  const { widthPx, heightPx, scale } = o
  if (!(widthPx > 0) || !(heightPx > 0) || !(scale > 0)) return []
  const { minorM } = gridStep(scale, o.zoom)
  const step = minorM * scale
  const [vx0, vy0, vx1, vy1] = o.view ?? [0, 0, widthPx, heightPx]
  const x0 = Math.max(0, vx0)
  const y0 = Math.max(0, vy0)
  const x1 = Math.min(widthPx, vx1)
  const y1 = Math.min(heightPx, vy1)
  const out: GridDot[] = []
  if (x1 < x0 || y1 < y0) return out
  const eps = 1e-6
  for (let i = Math.max(0, Math.ceil(x0 / step - eps)); i * step <= x1 + eps; i++) {
    for (let j = Math.max(0, Math.ceil(y0 / step - eps)); j * step <= y1 + eps; j++) {
      out.push({ x: i * step, y: j * step, major: i % GRID_MAJOR_EVERY === 0 && j % GRID_MAJOR_EVERY === 0 })
    }
  }
  return out
}

/**
 * Bara de scara: cea mai lunga lungime "rotunda" care incape in `maxPx`
 * pixeli de ecran.
 */
export function scaleBar(scale: number, zoom: number, maxPx = 120): { meters: number; px: number } | null {
  if (!(scale > 0) || !(zoom > 0)) return null
  const pxPerM = scale * zoom
  let best: number | null = null
  for (const m of [0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000]) {
    if (m * pxPerM <= maxPx) best = m
  }
  if (best === null) return null
  return { meters: best, px: Math.round(best * pxPerM) }
}

/** O valoare in pixeli ai foii prinsa pe pasul grilei (`stepPx`). */
export function snapPx(v: number, stepPx: number): number {
  return stepPx > 0 ? Math.round(v / stepPx) * stepPx : v
}

/** Latura etajului, in metri, din ce a scris omul; null = nu e un numar intre limite. */
export function canvasSide(raw: string): number | null {
  const v = Number(raw.trim().replace(',', '.'))
  if (raw.trim() === '' || !Number.isFinite(v) || v < CANVAS_MIN_M || v > CANVAS_MAX_M) return null
  return Math.round(v * 10) / 10
}
